import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { 
  TrendingUp, 
  TrendingDown, 
  Target, 
  ShieldAlert, 
  Loader2, 
  Search,
  BookOpen,
  AlertTriangle,
  Eye,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useDeepAnalysis, TradingParams, DeepAnalysis } from '@/hooks/useDeepAnalysis';
import { Link } from 'react-router-dom';

interface SwingTradeResultProps {
  analysis: DeepAnalysis;
  onNewSearch: () => void;
}

function SwingTradeResult({ analysis, onNewSearch }: SwingTradeResultProps) {
  const { swingTradeSetup, technicalPicture, positionSizing, keyRisks, whatToWatch, educationalNote, tokenInfo, marketData } = analysis;
  
  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case 'STRONG_BUY': return 'bg-success text-success-foreground';
      case 'BUY': return 'bg-success/80 text-success-foreground';
      case 'HOLD': return 'bg-warning text-warning-foreground';
      case 'SELL': return 'bg-destructive/80 text-destructive-foreground';
      case 'STRONG_SELL': return 'bg-destructive text-destructive-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatPrice = (price: number) => {
    if (price < 0.01) return `A$${price.toFixed(6)}`;
    if (price < 1) return `A$${price.toFixed(4)}`;
    if (price < 100) return `A$${price.toFixed(2)}`;
    return `A$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-4">
      {/* Header with Token Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold text-foreground">{tokenInfo.name}</h3>
          <Badge variant="outline">{tokenInfo.symbol}</Badge>
          <Badge className={getVerdictColor(analysis.verdict)}>{analysis.verdict.replace('_', ' ')}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/coin-reviewer?coin=${tokenInfo.id}`}>
            <Button variant="outline" size="sm" className="gap-2">
              <Eye className="w-4 h-4" />
              Full Analysis
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={onNewSearch}>
            <Search className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
      </div>

      {/* Current Price */}
      <div className="flex items-center gap-4 p-3 bg-card/50 rounded-lg border border-border">
        <div>
          <span className="text-2xl font-bold text-foreground">{formatPrice(marketData.price)}</span>
          <span className={`ml-2 text-sm ${marketData.priceChange24h >= 0 ? 'text-success' : 'text-destructive'}`}>
            {marketData.priceChange24h >= 0 ? '+' : ''}{marketData.priceChange24h.toFixed(2)}%
          </span>
        </div>
        {analysis.currentState?.keyObservation && (
          <p className="text-sm text-muted-foreground flex-1">{analysis.currentState.keyObservation}</p>
        )}
      </div>

      {/* Swing Trade Setup */}
      {swingTradeSetup && (
        <Card className="border-primary/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Swing Trade Setup
              </CardTitle>
              <Badge variant={swingTradeSetup.hasValidSetup ? 'default' : 'secondary'}>
                {swingTradeSetup.setupType.replace('_', ' ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {swingTradeSetup.hasValidSetup ? (
              <>
                {/* Entry Zone */}
                <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-primary">Entry Zone</span>
                    <span className="font-bold text-foreground">
                      {formatPrice(swingTradeSetup.entryZone.low)} - {formatPrice(swingTradeSetup.entryZone.high)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{swingTradeSetup.entryZone.explanation}</p>
                </div>

                {/* Take Profit Targets */}
                <div className="space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">Take Profit Targets</span>
                  {swingTradeSetup.takeProfitTargets?.map((target, i) => (
                    <div key={i} className="p-2 bg-success/10 rounded-lg border border-success/20 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-success" />
                        <span className="text-sm text-muted-foreground">{target.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold text-foreground">{formatPrice(target.price)}</span>
                        <span className="text-xs text-success ml-2">(+{target.percentGain.toFixed(1)}%)</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Stop Loss */}
                <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-destructive" />
                      <span className="text-sm font-medium text-destructive">Stop Loss</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-foreground">{formatPrice(swingTradeSetup.stopLoss.price)}</span>
                      <span className="text-xs text-destructive ml-2">(-{swingTradeSetup.stopLoss.percentRisk.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{swingTradeSetup.stopLoss.explanation}</p>
                </div>

                {/* Risk/Reward */}
                <div className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                  <span className="text-muted-foreground">Risk/Reward Ratio</span>
                  <span className="font-medium text-foreground">{swingTradeSetup.riskRewardRatio}</span>
                </div>
              </>
            ) : (
              <div className="p-4 bg-warning/10 rounded-lg border border-warning/20 text-center">
                <AlertTriangle className="w-8 h-8 text-warning mx-auto mb-2" />
                <p className="text-sm font-medium text-warning">No Clear Setup</p>
                {swingTradeSetup.waitingFor && (
                  <p className="text-xs text-muted-foreground mt-1">Waiting for: {swingTradeSetup.waitingFor}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Position Sizing & Risks Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Position Sizing */}
        {positionSizing && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Position Sizing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Recommendation</span>
                  <Badge variant="outline">{positionSizing.recommendation}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Max Position</span>
                  <span className="font-medium">{positionSizing.maxPositionPercent}% of portfolio</span>
                </div>
                <p className="text-xs text-muted-foreground border-t border-border pt-2 mt-2">
                  {positionSizing.dollarRiskExample}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Risks */}
        {keyRisks && keyRisks.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" />
                Key Risks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {keyRisks.slice(0, 3).map((risk, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] ${
                        risk.severity === 'High' ? 'border-destructive text-destructive' :
                        risk.severity === 'Medium' ? 'border-warning text-warning' :
                        'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {risk.severity}
                    </Badge>
                    <span className="text-muted-foreground">{risk.risk}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Educational Note */}
      {educationalNote && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm">{educationalNote.concept}</h4>
                <p className="text-xs text-muted-foreground mt-1">{educationalNote.explanation}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* What to Watch */}
      {whatToWatch && whatToWatch.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">Watch:</span>
          {whatToWatch.map((item, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {item}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export function SwingTradeWidget() {
  const { analyzeToken, clearAnalysis, analysis, isAnalyzing, error } = useDeepAnalysis();
  const [coinSearch, setCoinSearch] = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [hasPosition, setHasPosition] = useState(false);
  const [positionAmount, setPositionAmount] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tradingParams, setTradingParams] = useState<TradingParams>({
    riskTolerance: 'Moderate',
    timeHorizon: '1-2 weeks',
    portfolioRisk: 5
  });

  const handleAnalyze = async () => {
    if (!coinSearch.trim()) return;
    await analyzeToken(coinSearch.toLowerCase().trim(), tradingParams);
  };

  const handleNewSearch = () => {
    clearAnalysis();
    setCoinSearch('');
    setCurrentPrice('');
    setHasPosition(false);
    setPositionAmount('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isAnalyzing) {
      handleAnalyze();
    }
  };

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Swing Trade Analyzer
          </CardTitle>
          <Link to="/coin-reviewer">
            <Button variant="ghost" size="sm" className="gap-1 text-xs">
              Full Reviewer <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          All prices in AUD • Data from CoinGecko
        </p>
      </CardHeader>
      <CardContent>
        {!analysis && !isAnalyzing ? (
          <div className="space-y-4">
            {/* Token Search */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Enter ticker from Swyftx (e.g., BTC, SOL, RNDR)"
                    value={coinSearch}
                    onChange={(e) => setCoinSearch(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="pl-10"
                  />
                </div>
                <Button onClick={handleAnalyze} disabled={!coinSearch.trim() || isAnalyzing}>
                  Analyze
                </Button>
              </div>
              
              {/* Validation Help */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Current price on Swyftx (optional, for validation)"
                  value={currentPrice}
                  onChange={(e) => setCurrentPrice(e.target.value)}
                  className="flex-1 h-8 text-sm"
                  type="number"
                  step="any"
                />
                <span className="text-xs text-muted-foreground">A$</span>
              </div>
            </div>

            {/* Trading Parameters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="space-y-2">
                <Label className="text-xs">Risk Tolerance</Label>
                <Select
                  value={tradingParams.riskTolerance}
                  onValueChange={(value: 'Conservative' | 'Moderate' | 'Aggressive') => 
                    setTradingParams(p => ({ ...p, riskTolerance: value }))
                  }
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Conservative">Conservative</SelectItem>
                    <SelectItem value="Moderate">Moderate</SelectItem>
                    <SelectItem value="Aggressive">Aggressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Time Horizon</Label>
                <Select
                  value={tradingParams.timeHorizon}
                  onValueChange={(value) => setTradingParams(p => ({ ...p, timeHorizon: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-2 weeks">1-2 weeks</SelectItem>
                    <SelectItem value="2-4 weeks">2-4 weeks</SelectItem>
                    <SelectItem value="1-3 months">1-3 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Portfolio Risk: {tradingParams.portfolioRisk}%</Label>
                <Slider
                  value={[tradingParams.portfolioRisk]}
                  onValueChange={(value) => setTradingParams(p => ({ ...p, portfolioRisk: value[0] }))}
                  min={1}
                  max={20}
                  step={1}
                  className="mt-3"
                />
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {showAdvanced ? '▼' : '▶'} Current Position (optional)
            </button>

            {/* Current Position - Advanced */}
            {showAdvanced && (
              <div className="p-3 bg-muted/20 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <Label className="text-xs whitespace-nowrap">Already holding?</Label>
                  <Select
                    value={hasPosition ? 'yes' : 'no'}
                    onValueChange={(v) => setHasPosition(v === 'yes')}
                  >
                    <SelectTrigger className="h-8 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasPosition && (
                    <Input
                      placeholder="Amount held"
                      value={positionAmount}
                      onChange={(e) => setPositionAmount(e.target.value)}
                      className="h-8 w-32 text-sm"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Quick Examples */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground">Popular:</span>
              {['bitcoin', 'ethereum', 'solana', 'render-token', 'chainlink'].map(coin => (
                <Button
                  key={coin}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => setCoinSearch(coin)}
                >
                  {coin.replace('-token', '')}
                </Button>
              ))}
            </div>

            {/* Validation Help Text */}
            <p className="text-xs text-muted-foreground">
              💡 <strong>Tip:</strong> Use CoinGecko ID for best results. For RENDER token, use "render-token". 
              If the token isn't found, we'll suggest alternatives.
            </p>

            {error && (
              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive whitespace-pre-line">{error}</p>
                </div>
              </div>
            )}
          </div>
        ) : isAnalyzing ? (
          <div className="py-12 text-center">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium text-foreground">Analyzing {coinSearch}...</p>
            <p className="text-sm text-muted-foreground">Validating token & gathering AUD market data</p>
          </div>
        ) : analysis ? (
          <SwingTradeResult analysis={analysis} onNewSearch={handleNewSearch} />
        ) : null}
      </CardContent>
    </Card>
  );
}
